/**
 * TayTae Offer Popup — 10% off a first order
 * - Fires on whichever comes first: a time delay, a scroll depth, or exit intent
 *   (desktop only; phones have no exit intent, so scroll depth carries them)
 * - Never interrupts an active purchase: suppressed on cart/checkout and while
 *   the cart drawer is open
 * - Shows on first 3 visits, then snoozes 10 days; once signed up, never again
 * - Submits to the Shopify customer list, tagged with the kid's sizes
 *
 * Uses event delegation so handlers survive Shopify dev server hot-reloads.
 */
(function () {
  'use strict';

  var STORAGE_SIGNED_UP = 'taytae_offer_signed_up';
  var STORAGE_COUNT     = 'taytae_waitlist_count';
  var STORAGE_SNOOZE    = 'taytae_waitlist_snooze_until';
  var MAX_VIEWS         = 3;
  var SNOOZE_DAYS       = 10;

  // ── Helper: get popup elements (queried fresh each time for hot-reload safety) ──
  function getPopup()     { return document.getElementById('waitlistPopup'); }
  function getForm()      { return document.getElementById('waitlistForm'); }
  function getFormView()  { return document.getElementById('waitlistFormView'); }
  function getSuccess()   { return document.getElementById('waitlistSuccess'); }
  function getError()     { return document.getElementById('waitlistError'); }
  function getSubmitBtn() { return document.getElementById('waitlistSubmitBtn'); }

  // ── Show / hide popup ──
  function showPopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    var firstInput = popup.querySelector('input');
    if (firstInput) firstInput.focus();
  }

  function hidePopup() {
    var popup = getPopup();
    if (!popup) return;
    popup.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  // ── Show success state ──
  function showSuccess() {
    var formView = getFormView();
    var successView = getSuccess();
    if (formView) formView.style.display = 'none';
    if (successView) successView.classList.add('is-visible');

    // Mark as permanently signed up — popup will never show again
    localStorage.setItem(STORAGE_SIGNED_UP, '1');
  }

  // ── Show error state ──
  function showError() {
    var submitBtn = getSubmitBtn();
    var errorView = getError();
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.getAttribute('data-default-label') || 'Join the Waitlist';
    }
    if (errorView) errorView.classList.add('is-visible');
  }

  // ── Form submit — capture phase so we intercept before anything else ──
  document.addEventListener('submit', function (e) {
    // Only handle our waitlist form
    var form = e.target;
    if (form.id !== 'waitlistForm') return;

    // Prevent native form submission — belt and suspenders
    e.preventDefault();
    e.stopPropagation();
    // Stash the real action URL before neutralizing the form
    if (!form.getAttribute('data-action')) {
      form.setAttribute('data-action', form.action || '/contact');
    }
    // Neutralize the form's action so nothing can navigate away (# is CSP-safe)
    form.setAttribute('action', '#');

    var firstName = document.getElementById('waitlistName').value.trim();
    var email     = document.getElementById('waitlistEmail').value.trim();

    // Basic validation
    if (!firstName || !email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    // Spam guard — honeypot + time trap. This handler preventDefaults, so
    // form-guard.js's native listener never fires here. Silently show success
    // rather than an error; a bot told it failed just adapts.
    if (window.TayTaeFormGuard && window.TayTaeFormGuard.looksLikeBot(form)) {
      showSuccess();
      return;
    }

    // Collect selected sizes
    var sizeCheckboxes = form.querySelectorAll('input[name="sizes[]"]:checked');
    var sizes = Array.from(sizeCheckboxes).map(function (cb) { return cb.value; });

    // Build tags: marketing list + the offer they came in on + one tag per size
    var popupEl = getPopup();
    var offerCode = popupEl ? (popupEl.getAttribute('data-offer-code') || '') : '';
    var tags = ['newsletter'];
    if (offerCode) tags.push(offerCode.toLowerCase());
    sizes.forEach(function (size) {
      tags.push('size-' + size.toLowerCase().replace(/[^a-z0-9]/g, ''));
    });

    // Disable submit while processing
    var submitBtn = getSubmitBtn();
    if (submitBtn) {
      // Stash original label for error recovery
      if (!submitBtn.getAttribute('data-default-label')) {
        submitBtn.setAttribute('data-default-label', submitBtn.textContent);
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Joining...';
    }

    // Hide any previous error
    var errorView = getError();
    if (errorView) errorView.classList.remove('is-visible');

    // Generate a random password — required by Shopify's create_customer form.
    // The customer won't know this password (they don't need an account, just a waitlist entry).
    var randomPassword = 'wl_' + Math.random().toString(36).slice(2) + Date.now().toString(36);

    // Build FormData from the Shopify form (includes authenticity_token, form_type, utf8)
    var formData = new FormData(form);
    formData.set('customer[first_name]', firstName);
    formData.set('customer[email]', email);
    formData.set('customer[tags]', tags.join(','));
    formData.set('customer[password]', randomPassword);

    // Use the form's own action URL (generated by {% form 'customer' %})
    var actionUrl = form.getAttribute('data-action') || '/contact';

    // ── Submit 1: Klaviyo (this is the one that actually sends the email) ──
    // Shopify does NOT apply customer[tags] — measured, 39 of 40 customers have
    // none — and the Shopify -> Klaviyo sync is not running, so the Shopify POST
    // alone reaches nobody. See assets/klaviyo-subscribe.js for the full story.
    var klaviyoSubmit = (window.TayTaeKlaviyo && window.TayTaeKlaviyo.subscribe)
      ? window.TayTaeKlaviyo.subscribe({
          email: email,
          firstName: firstName,
          sizes: sizes,
          source: 'Offer Popup' + (offerCode ? ' (' + offerCode + ')' : '')
        })
      : Promise.resolve(false);

    // ── Submit 2: Shopify (keeps the customer record) ──────────────────────
    var shopifySubmit = fetch(actionUrl, {
      method: 'POST',
      body: formData,
      redirect: 'manual'
    })
      .then(function (response) {
        // With redirect:'manual', a 302 comes back as type 'opaqueredirect' (status 0).
        // Any non-server-error or opaque redirect means the submission was accepted.
        return response.type === 'opaqueredirect' || response.ok || response.status < 500;
      })
      .catch(function () { return false; });

    // Show success if EITHER path worked — a subscriber is worth more than a
    // tidy failure, and the two paths fail independently.
    Promise.all([klaviyoSubmit, shopifySubmit]).then(function (results) {
      if (results[0] || results[1]) {
        showSuccess();
      } else {
        showError();
      }
    });
  }, true);

  // ── Close handlers — delegated ──
  document.addEventListener('click', function (e) {
    // Close button
    if (e.target.closest('#waitlistClose')) {
      hidePopup();
      return;
    }
    // Backdrop click
    if (e.target.id === 'waitlistBackdrop') {
      hidePopup();
      return;
    }
    // "Can't Wait!" dismiss button on success screen
    if (e.target.closest('#waitlistSuccessDismiss')) {
      hidePopup();
      return;
    }
  });

  // Escape key
  document.addEventListener('keydown', function (e) {
    var popup = getPopup();
    if (e.key === 'Escape' && popup && popup.classList.contains('is-active')) {
      hidePopup();
    }
  });

  // ── Tap-to-copy the discount code ──
  document.addEventListener('click', function (e) {
    var chip = e.target.closest('#waitlistCode');
    if (!chip) return;
    var code = chip.getAttribute('data-code') || '';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(function () {
        chip.classList.add('is-copied');
      }).catch(function () {});
    }
  });

  // A shopper reading a product page is the closest to buying; a modal over the
  // size chart or the Add to Cart costs more than the email is worth.
  function isProductPage() {
    return /^\/products\//.test(window.location.pathname) ||
           document.body.classList.contains('template-product');
  }

  // ── Should we stay out of the way entirely? ──
  function isBadMoment() {
    // Never interrupt someone who is already checking out
    var path = window.location.pathname;
    if (/^\/(cart|checkout)/.test(path)) return true;
    // Nor someone actively looking at their cart
    var drawer = document.getElementById('cartDrawer');
    if (drawer && drawer.classList.contains('is-open')) return true;
    return false;
  }

  // ── Auto-show logic: visit count / snooze / signup status, then triggers ──
  function init() {
    var popup = getPopup();
    if (!popup) return;

    // Already signed up — never show again
    if (localStorage.getItem(STORAGE_SIGNED_UP)) return;

    // Check if currently snoozed
    var snoozeUntil = parseInt(localStorage.getItem(STORAGE_SNOOZE) || '0', 10);
    if (snoozeUntil) {
      if (Date.now() < snoozeUntil) return;
      // Snooze expired — reset count and start a fresh 3-visit cycle
      localStorage.removeItem(STORAGE_SNOOZE);
      localStorage.setItem(STORAGE_COUNT, '0');
    }

    // Check how many times the popup has already been shown
    var count = parseInt(localStorage.getItem(STORAGE_COUNT) || '0', 10);
    if (count >= MAX_VIEWS) {
      localStorage.setItem(STORAGE_SNOOZE, Date.now() + SNOOZE_DAYS * 864e5);
      return;
    }

    var delayMs      = (parseInt(popup.getAttribute('data-delay') || '25', 10) || 25) * 1000;
    var scrollPct    = parseInt(popup.getAttribute('data-scroll-percent') || '40', 10);
    var wantExit     = popup.getAttribute('data-exit-intent') === 'true';
    // Exit intent needs a real cursor; phones and tablets have none.
    var canExitIntent = wantExit && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    // On a product page, only exit intent may fire: no timer, no scroll trigger.
    // Scrolling a product page means reading the details, not losing interest.
    var onProduct = isProductPage();

    var fired = false;

    function fire() {
      if (fired) return;
      if (isBadMoment()) return;   // try again on a later trigger
      fired = true;
      cleanup();
      // Only count a view once it is actually shown
      localStorage.setItem(STORAGE_COUNT, count + 1);
      showPopup();
    }

    var timer = onProduct ? null : setTimeout(fire, delayMs);

    function onScroll() {
      if (scrollPct <= 0) return;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = (window.pageYOffset / scrollable) * 100;
      if (pct >= scrollPct) fire();
    }

    function onMouseOut(e) {
      // Cursor left through the top of the window — classic abandon signal
      if (!e.relatedTarget && e.clientY <= 0) fire();
    }

    function cleanup() {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
    }

    if (scrollPct > 0 && !onProduct) window.addEventListener('scroll', onScroll, { passive: true });
    if (canExitIntent) document.addEventListener('mouseout', onMouseOut);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
